/**
 * useTaskCategories - Hook para gerenciamento de categorias de tarefas
 * 
 * Fornece queries e mutations para CRUD de categorias via RPCs server-side.
 * 
 * @version 1.5.0
 * RELEASE: 2026-10-10 v1.5.0 - Permite forçar a estrutura de Ordem de Serviço
 *          em componentes de navegação fora da árvore principal de rotas.
 * RELEASE: 2026-10-08 v1.4.0 - Rotas da Ordem de Serviço usam get/upsert/delete/reorder_service_order_categories.
 * RELEASE: 2026-04-06 v1.0.0 - Criação do hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { useTaskModuleType, useIsServiceOrderStructure } from '@/hooks/useTaskModuleType';

// Logger para depuração
const logger = createLogger('useTaskCategories');

// Tipo da categoria retornada pela RPC
export interface TaskCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  phases_count: number;
  tasks_count: number;
  created_at: string;
  updated_at: string;
  /** v1.1.0: marcador "Padrão" do módulo (opcional) */
  is_default?: boolean;
  /** 2026-10-22: perfil pode visualizar ao menos uma fase (Permissões por Fase) */
  has_visible_phase?: boolean;
}

// Tipo para criação/atualização de categoria
export interface UpsertTaskCategoryInput {
  p_id?: string;
  p_name: string;
  p_description?: string;
  p_color?: string;
  p_sort_order?: number;
  p_is_active?: boolean;
  /** v1.1.0: marca a categoria como padrão do módulo (opcional) */
  p_is_default?: boolean;
}

/**
 * Hook principal para categorias de tarefas
 *
 * @param onlyActive - Retorna apenas categorias ativas
 * @param taskTypeOverride - (v1.2.0) Força o módulo (normal | retention | legal)
 *        em vez de derivar da rota. Usado quando a tela precisa respeitar o tipo
 *        de UMA tarefa específica (ex.: modal "Alterar Fase").
 */
export function useTaskCategories(
  onlyActive: boolean = false,
  taskTypeOverride?: 'normal' | 'retention' | 'legal',
  serviceOrdersOverride: boolean = false,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  // Módulo corrente (normal | retention | legal) derivado da rota ou override
  const routeTaskType = useTaskModuleType();
  const taskType = taskTypeOverride ?? routeTaskType;
  // v1.4.0: Ordem de Serviço usa as mesmas telas com RPCs próprias (sem override de módulo)
  const isOS = serviceOrdersOverride || (useIsServiceOrderStructure() && !taskTypeOverride);
  const tenantHint = currentTenant?.id ?? null;

  // Query para listar categorias
  const categoriesQuery = useQuery({
    queryKey: ['task-categories', currentTenant?.id, onlyActive, isOS ? 'service_order' : taskType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'get_service_order_categories', {
            p_only_active: onlyActive,
            p_tenant_hint: tenantHint,
          })
        : await supabase.rpc('get_task_categories', {
            p_only_active: onlyActive,
            p_task_type: taskType, // escopo do módulo corrente
          } as any);

      if (error) {
        logger.error('Erro ao buscar categorias de tarefas', { error: error.message });
        throw error;
      }

      return (data || []) as TaskCategory[];
    },
    enabled: !!currentTenant?.id,
  });

  // Mutation para criar/atualizar categoria
  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertTaskCategoryInput) => {
      const { data, error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'upsert_service_order_category', {
            ...input,
            p_tenant_hint: tenantHint,
          })
        : await supabase.rpc('upsert_task_category', {
            ...input,
            // Categoria sempre nasce/permanece no módulo em que o usuário está
            p_task_type: taskType,
          } as any);

      if (error) {
        logger.error('Erro ao salvar categoria de tarefa', { error: error.message });
        throw error;
      }

      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-categories'] });
      toast.success(variables.p_id ? 'Categoria atualizada com sucesso' : 'Categoria criada com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao salvar categoria: ${error.message}`);
    },
  });

  // Mutation para excluir categoria
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'delete_service_order_category', { p_id: id, p_tenant_hint: tenantHint })
        : await supabase.rpc('delete_task_category', { p_id: id });

      if (error) {
        logger.error('Erro ao excluir categoria de tarefa', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-categories'] });
      toast.success('Categoria excluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir categoria: ${error.message}`);
    },
  });

  /**
   * Mutation para reordenar categorias (abas do Kanban) — v1.3.0
   *
   * Recebe os IDs já na ordem desejada e persiste via RPC segura
   * `reorder_task_categories`, escopada no módulo corrente (task_type).
   */
  const reorderMutation = useMutation({
    mutationFn: async (categoryIds: string[]) => {
      const { error } = isOS
        ? await (supabase.rpc as any).call(supabase, 'reorder_service_order_categories', {
            p_category_ids: categoryIds,
            p_tenant_hint: tenantHint,
          })
        : await supabase.rpc('reorder_task_categories', {
            p_category_ids: categoryIds,
            p_task_type: taskType,
          } as any);

      if (error) {
        logger.error('Erro ao reordenar categorias de tarefas', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-categories'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reordenar categorias: ${error.message}`);
    },
  });

  return {
    categories: categoriesQuery.data || [],
    isLoading: categoriesQuery.isLoading,
    error: categoriesQuery.error,
    refetch: categoriesQuery.refetch,
    upsert: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
    // v1.3.0: reordenação das abas por drag and drop
    reorder: reorderMutation.mutateAsync,
    isReordering: reorderMutation.isPending,
  };

}
